import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';
import { ArrowRight, Clock, Tag } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function BlogPage() {
  const featured = blogPosts.find((p) => p.featured);
  const rest = blogPosts.filter((p) => !p.featured);

  return (
    <MarketingLayout>
      <SEO
        title="Blog"
        description="Expert guides on H-1B, H-1B1, and E-3 Public Access File compliance. Learn about PAF requirements, DOL audits, LCA posting rules, and prevailing wage documentation."
        path="/blog"
        keywords="H-1B public access file guide, PAF compliance blog, DOL audit preparation, LCA posting requirements, prevailing wage documentation"
      />

      {/* SEO-only H1 (visually hidden, fully accessible to search engines & screen readers) */}
      <h1 className="sr-only">PAFHub — Simplify H-1B, H-1B1 & E-3 Public Access Files(PAF) Compliance</h1>

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">PAFHub Blog</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">
              Public Access File <span className="bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">Knowledge Base</span>
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Expert guides on H-1B, H-1B1, and E-3 compliance. Everything employers need to know about managing Public Access Files and staying audit-ready.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      {featured && (
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
              <Link to={`/blog/${featured.slug}`} className="group block">
                <div className="relative rounded-2xl border border-indigo-100/60 bg-gradient-to-br from-indigo-50/40 to-sky-50/20 p-8 lg:p-12 hover:shadow-lg hover:shadow-indigo-100/50 transition-all">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-full">Featured</span>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">{featured.category}</span>
                  </div>
                  <h2 data-testid="featured-blog-title" className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-3 group-hover:text-indigo-600 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-[#64748B] text-base lg:text-lg leading-relaxed mb-6 max-w-3xl">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-[#94A3B8]">
                    <span>{featured.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.readTime}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-indigo-600 font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section className="py-12 lg:py-20 bg-[#F8FAFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-8">All Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post, i) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <Link to={`/blog/${post.slug}`} className="group block h-full">
                  <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 h-full flex flex-col hover:shadow-md hover:shadow-indigo-100/40 transition-all">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full">
                        <Tag className="w-3 h-3" /> {post.category}
                      </span>
                    </div>
                    <h3 data-testid={`blog-card-${post.slug}`} className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-indigo-600 transition-colors leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-sm text-[#64748B] leading-relaxed mb-4 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-[#94A3B8] pt-4 border-t border-slate-100">
                      <span>{post.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-3">
              Stop reading about compliance. Start managing it.
            </h2>
            <p className="text-indigo-200/70 text-base mb-8">
              Create your free PAFHub account and get your Public Access Files organized in minutes.
            </p>
            <Link
              to="/login"
              data-testid="blog-cta-get-started"
              className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg shadow-indigo-500/25 text-[15px]"
            >
              Get Started Free <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
